import { action } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import {
  equipDecorationItem,
  markDecorationItemsAsSeen,
  purchaseDecorationItem,
  unequipAllDecorationItems,
} from './decoration.modifiers';
import type {
  DecorationItemID,
  DecorationRoomID,
  DecorationTierID,
} from './decoration.ruleset';
import { getDecorationItem, isDecorationPurchased } from './decoration.getters';
import { getCurrencyConsumeEvent } from '../analytics/events';
import * as Inventory from '../inventory';

export default createActions({
  /**
   * Equip decoration items for specified room
   */
  equipDecorationItems: action(
    (
      state,
      args: {
        room: DecorationRoomID;
        items: { itemId: DecorationItemID; tierId?: DecorationTierID }[];
      },
    ) => {
      const { room, items } = args;

      // Clear all selected items for room ID
      unequipAllDecorationItems(state, room);

      for (const { itemId, tierId } of items) {
        const item = getDecorationItem(itemId);
        if (!item)
          throw new Error(`Decoration item ${itemId} not found in config`);

        const isPurchased = isDecorationPurchased(state, itemId);
        const isPaid = item.price > 0;

        // If item cost money but not purchased throw error
        if (isPaid && !isPurchased) {
          throw new Error(`Decoration item ${itemId} was not purchased`);
        }

        // Is item already purchased or free
        equipDecorationItem(state, itemId, room, item.slot, tierId);
      }
    },
  ),

  /**
   * Purchase decoration items
   */
  purchaseDecorationItems: action((state, ids: DecorationItemID[], api) => {
    for (const id of ids) {
      const now = api.date.now();

      // Purchase
      purchaseDecorationItem(
        state,
        id,
        now,
        {
          feature: 'decoration',
          $subFeature: id,
        },
        api,
      );
    }
  }),

  /**
   * Mark all decoration item as seen.
   */
  markDecorationItemsAsSeen: action((state) => {
    markDecorationItemsAsSeen(state);
  }),
});
